import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Button, Group, Stack, Text } from '@mantine/core';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DS_AI_MOTION, DS_AI_TABULAR_FIGURES } from '../../../../theme/dealstackAi.tokens';
import { dsAiTypeStyle } from '../../../../theme/dealstackAi.typography';
import {
	countUp,
	fadeRise,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.animations';
import type { SourceFooterState } from '../../useExtractFromDocumentModalController.types';

interface Props {
	footer: SourceFooterState;
	onCancel: () => void;
}

/** Footer: the uploaded or selected files with their pages towards the batch cap, Cancel and "Extract transactions", with a hint while nothing is added. */
export const SourceFooter = ({ footer, onCancel }: Props) => {
	const tCapManage = useTranslationCapManage();

	return (
		<Group
			gap="sm"
			wrap="nowrap"
			mih="4.5rem"
			p="sm"
			bg="gray.0"
			style={{ borderTop: '1px solid var(--mantine-color-gray-4)' }}
		>
			{footer.count > 0 && (
				<Group
					gap="xs"
					wrap="nowrap"
					c="dark.0"
					aria-live="polite"
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_FOOTER_COUNT}
					styles={{
						root: {
							animation: `${fadeRise} ${DS_AI_MOTION.baseMs}ms ${DS_AI_MOTION.easeOut} both`,
						},
					}}
				>
					<Text
						span
						key={footer.count}
						display="inline-block"
						styles={{
							root: {
								...dsAiTypeStyle('sans/30/600'),
								...DS_AI_TABULAR_FIGURES,
								animation: `${countUp} ${DS_AI_MOTION.baseMs}ms ${DS_AI_MOTION.easeOut} both`,
							},
						}}
					>
						{footer.count}
					</Text>
					<Stack
						component="span"
						gap={0}
						fz="md"
						styles={{ root: { '& strong': { fontWeight: 600 } } }}
					>
						<strong>{tCapManage('dealstack_ai.ingest.footer.files')}</strong>
						<span>
							{tCapManage(
								footer.unit === 'selected'
									? 'dealstack_ai.ingest.footer.selected'
									: 'dealstack_ai.ingest.footer.uploaded'
							)}
						</span>
					</Stack>
					<Text
						span
						size="md"
						pl="xs"
						styles={{ root: DS_AI_TABULAR_FIGURES }}
						style={{ borderLeft: '1px solid var(--mantine-color-gray-4)' }}
						data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_FOOTER_PAGES}
					>
						{footer.pages === null
							? tCapManage('dealstack_ai.ingest.footer.counting_pages')
							: tCapManage('dealstack_ai.ingest.footer.pages', {
									count: footer.pages,
								})}
					</Text>
				</Group>
			)}
			<Group gap="sm" align="center" justify="flex-start" wrap="nowrap" ml="auto">
				<Button
					size="height-36"
					variant="link-grey"
					fw={600}
					px={0}
					onClick={onCancel}
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_CANCEL_BUTTON}
				>
					{tCapManage('dealstack_ai.ingest.footer.cancel')}
				</Button>
				{footer.count === 0 && (
					<Text span fz="sm" c="dark.0">
						{tCapManage('dealstack_ai.ingest.footer.empty_hint')}
					</Text>
				)}
				<Button
					size="height-36"
					fw={600}
					disabled={!footer.canAnalyse}
					loading={footer.isStarting}
					onClick={footer.onAnalyse}
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_ANALYSE_BUTTON}
				>
					{tCapManage('dealstack_ai.ingest.footer.analyse')}
				</Button>
			</Group>
		</Group>
	);
};
